import { effect, inject, Injectable, signal, untracked } from '@angular/core';
import {
  AgendaDataService,
  ProfissionalDataService,
  TipoServicoDataService,
} from '@nomoh-monorepo/data-access-bardo-administrador';
import { IAgenda, StatusAgendamentoEnum } from '@nomoh-monorepo/shared-types';
import { SelectItemGroup } from 'primeng/api';

interface FiltroDisponivel {
  nome: string;
  cor: string;
  status: StatusAgendamentoEnum;
  chaveTraducao: string;
}

@Injectable({
  providedIn: 'root',
})
export class AgendaFiltroService {
  //#region Filtros disponíveis
  filtrosDisponiveis: FiltroDisponivel[] = [
    {
      nome: 'Realizado',
      cor: 'realizado',
      status: StatusAgendamentoEnum.REALIZADO,
      chaveTraducao: 'BARDO_STATUS.REALIZADO',
    },
    {
      nome: 'Confirmado',
      cor: 'confirmado',
      status: StatusAgendamentoEnum.CONFIRMADO,
      chaveTraducao: 'BARDO_STATUS.CONFIRMADO',
    },
    {
      nome: 'Cancelado',
      cor: 'cancelado',
      status: StatusAgendamentoEnum.CANCELADO,
      chaveTraducao: 'BARDO_STATUS.CANCELADO',
    },
    {
      nome: 'Agendado',
      cor: 'agendado',
      status: StatusAgendamentoEnum.PENDENTE,
      chaveTraducao: 'BARDO_STATUS.AGENDADO',
    },
  ];

  filtrosAtivos = signal<string[]>([]);
  grupoServicos = signal<SelectItemGroup[]>([]);
  profissionais = signal<{ nome: string; id: string }[]>([]);
  servicosSelecionados = signal<string[]>([]);
  profissionaisSelecionados = signal<string[]>([]);
  /**
   * Encaixe é ortogonal ao status (um encaixe confirmado continua sendo um
   * encaixe), por isso não entra em `filtrosDisponiveis`/`filtrosAtivos`.
   */
  filtroEncaixeAtivo = signal<boolean>(false);
  //#endregion

  //#region Injeções
  private readonly AgendaDataService = inject(AgendaDataService);
  private readonly tipoServicoDataService = inject(TipoServicoDataService);
  private readonly profissionalDataService = inject(ProfissionalDataService);
  //#endregion

  constructor() {
    effect(
      () => {
        const tiposServicos = this.tipoServicoDataService.$tiposServico();
        const grupos = (tiposServicos || [])
          .filter((tipo) => tipo.status !== false)
          .map((tipo) => ({
            label: tipo.nome,
            items:
              tipo.servicos
                ?.filter((servico) => servico.status !== false)
                .map((servico) => ({
                  label: servico.nome,
                  value: servico.id,
                })) || [],
          }))
          .filter((grupo) => grupo.items.length > 0);

        this.grupoServicos.set(grupos);
      }
    );

    effect(
      () => {
        const profissionais = this.profissionalDataService.$profissionais();
        if (!profissionais?.length) {
          this.profissionais.set([]);
          return;
        }

        const ativos = profissionais
          .filter((profissional) => profissional.status !== false)
          .map((profissional) => ({
            id: profissional.id,
            nome: profissional.nome,
          }));

        this.profissionais.set(ativos);
      }
    );

    effect(
      () => {
        // Sempre que a data principal mudar, os filtros são reiniciados
        this.AgendaDataService.$dataSelecionada();
        untracked(() => this.limparFiltros());
      }
    );
  }

  /**
   * Alterna o filtro "somente encaixes"
   */
  alternarFiltroEncaixe(): void {
    this.filtroEncaixeAtivo.set(!this.filtroEncaixeAtivo());
    this.filtrarAgendamento();
  }

  /**
   * Alterna o estado de um filtro de status
   */
  alternarFiltro(filtroNome: string): void {
    const ativos = [...this.filtrosAtivos()];
    const index = ativos.indexOf(filtroNome);

    if (index === -1) {
      ativos.push(filtroNome);
    } else {
      ativos.splice(index, 1);
    }

    this.filtrosAtivos.set(ativos);
    this.filtrarAgendamento();
  }

  /**
   * Filtra agendamentos com base nos filtros ativos
   */
  filtrarAgendamento(): void {
    const agendaOriginal = this.AgendaDataService.$agendaInDb();
    const agenda: IAgenda = {
      ...agendaOriginal,
      agendamentos: [...(agendaOriginal.agendamentos || [])],
    };

    // Filtro por status
    const statusFiltrar = this.filtrosAtivos()
      .map((nome) => {
        const filtro = this.filtrosDisponiveis.find((f) => f.nome === nome);
        return filtro?.status;
      })
      .filter(
        (status): status is StatusAgendamentoEnum => status !== undefined
      );

    if (statusFiltrar.length > 0) {
      agenda.agendamentos = agenda.agendamentos?.filter((agendamento) =>
        statusFiltrar.includes(agendamento.status)
      );
    }

    // Filtro por profissionais
    const profsSelecionados = this.profissionaisSelecionados();
    if (profsSelecionados.length > 0) {
      agenda.agendamentos = agenda.agendamentos?.filter((agendamento) =>
        profsSelecionados.includes(agendamento.profissionalId)
      );
    }

    // Filtro por serviços
    const servsSelecionados = this.servicosSelecionados();
    if (servsSelecionados.length > 0) {
      agenda.agendamentos = agenda.agendamentos?.filter((agendamento) =>
        servsSelecionados.includes(agendamento.servicoId)
      );
    }

    // Filtro por encaixe
    if (this.filtroEncaixeAtivo()) {
      agenda.agendamentos = agenda.agendamentos?.filter(
        (agendamento) => agendamento.isEncaixe
      );
    }

    // Atualiza flag de filtro preenchido
    const temFiltro =
      this.filtrosAtivos().length > 0 ||
      profsSelecionados.length > 0 ||
      servsSelecionados.length > 0 ||
      this.filtroEncaixeAtivo();

    this.AgendaDataService.setFiltroPreenchido(temFiltro);
    this.AgendaDataService.setAgenda(agenda);
  }

  /**
   * Limpa todos os filtros
   */
  limparFiltros(): void {
    this.filtrosAtivos.set([]);
    this.profissionaisSelecionados.set([]);
    this.servicosSelecionados.set([]);
    this.filtroEncaixeAtivo.set(false);
    this.filtrarAgendamento();
  }
}
